/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */
import { cssMap as cssMapUnbounded } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import { trackChangesMessages } from '@atlaskit/editor-common/messages';
import HistoryIcon from '@atlaskit/icon-lab/core/history';
import { fg } from '@atlaskit/platform-feature-flags';
import { Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { FeedbackForm } from '../../components/FeedbackForm/FeedbackForm';
import { FloatingContainer } from '../../components/FloatingContainer/FloatingContainer';

import messages from './messages';

const styles = cssMap({
	container: {
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
});

const stylesUnbounded = cssMapUnbounded({
	toggleChangesButtonWrapper: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'button::after': {
			border: 'none',
		},
	},
});

interface AcceptRejectProps {
	isDisplayingChanges?: boolean;
	onAccept: () => void;
	onBadResponseButtonClick: () => void;
	onGoodResponseButtonClick: () => void;
	onReject: () => void;
	onToggleChanges: () => void;
}

export const AcceptRejectScreen = ({
	onAccept,
	onReject,
	onBadResponseButtonClick,
	onGoodResponseButtonClick,
	isDisplayingChanges,
	onToggleChanges,
}: AcceptRejectProps) => {
	const { formatMessage } = useIntl();

	return (
		<FloatingContainer
			data-testid="accept-reject-screen"
			rainbowBorderAnimation={false}
			rainbowBorder={false}
			disablePadding
			enableMaxWidth
			isAIFC
		>
			<Flex justifyContent="space-between" alignItems="center" xcss={styles.container}>
				<Flex gap="space.050">
					{fg('platform_editor_ai_aifc_no_default_diff') && (
						<Flex
							alignItems="center"
							justifyContent="center"
							xcss={stylesUnbounded.toggleChangesButtonWrapper}
						>
							<IconButton
								testId="toggle-changes-button"
								type="button"
								icon={HistoryIcon}
								label={formatMessage(trackChangesMessages.toolbarIconLabel)}
								appearance="subtle"
								isTooltipDisabled={false}
								tooltip={{
									position: 'top',
									content: formatMessage(trackChangesMessages.toolbarIconLabel),
								}}
								isSelected={isDisplayingChanges}
								onClick={onToggleChanges}
							/>
						</Flex>
					)}
					<FeedbackForm
						onBadResponseButtonClick={onBadResponseButtonClick}
						onGoodResponseButtonClick={onGoodResponseButtonClick}
						flexIconWrapper={fg('platform_editor_ai_aifc_no_default_diff')}
						largeIconButton={fg('platform_editor_ai_aifc_no_default_diff')}
					/>
				</Flex>
				<Flex gap={fg('platform_editor_ai_aifc_no_default_diff') ? 'space.050' : 'space.100'}>
					<Button appearance="subtle" onClick={onReject}>
						{formatMessage(messages.rejectLabel)}
					</Button>
					<Button appearance="primary" onClick={onAccept} autoFocus>
						{formatMessage(messages.acceptLabel)}
					</Button>
				</Flex>
			</Flex>
		</FloatingContainer>
	);
};
